<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 px-3 py-4">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Dashboard</h1>
                                        <p class="text-sm text-gray-500 mt-1 m-0">
                                                Welcome back, <span class="font-semibold text-orange-7">{{ userName }}</span>
                                        </p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <span class="inline-flex items-center px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                                <span class="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
                                                System Online
                                        </span>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="w-full p-3">
                        <!-- Stats Cards -->
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                                <!-- Lister Items Card -->
                                <div 
                                        :class="[
                                                'bg-white rounded-xl shadow-sm p-5 transition-all duration-150',
                                                canAccessModule(moduleNames.listerItems) ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : 'opacity-60'
                                        ]"
                                        @click="navigateTo('/listeritem', moduleNames.listerItems)"
                                >
                                        <div class="flex items-center gap-4">
                                                <div class="w-12 h-12 bg-orange-2 rounded-xl flex items-center justify-center">
                                                        <svg class="w-6 h-6 text-orange-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                        </svg>
                                                </div>
                                                <div>
                                                        <p class="text-sm font-medium text-gray-500 m-0">Lister Items</p>
                                                        <p class="text-2xl font-semibold text-gray-900 mt-0.5 m-0">{{ stats.productGroups }}</p>
                                                </div>
                                        </div>
                                </div>

                                <!-- Offerings Card -->
                                <div 
                                        :class="[
                                                'bg-white rounded-xl shadow-sm p-5 transition-all duration-150',
                                                canAccessModule(moduleNames.offerings) ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : 'opacity-60'
                                        ]"
                                        @click="navigateTo('/offerings', moduleNames.offerings)"
                                >
                                        <div class="flex items-center gap-4">
                                                <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                                        <svg class="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                </div>
                                                <div>
                                                        <p class="text-sm font-medium text-gray-500 m-0">Offerings</p>
                                                        <p class="text-2xl font-semibold text-gray-900 mt-0.5 m-0">{{ stats.offerings }}</p>
                                                </div>
                                        </div>
                                </div>

                                <!-- PDPs Card -->
                                <div 
                                        :class="[
                                                'bg-white rounded-xl shadow-sm p-5 transition-all duration-150',
                                                canAccessModule(moduleNames.pdps) ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : 'opacity-60'
                                        ]"
                                        @click="navigateTo('/pdp', moduleNames.pdps)"
                                >
                                        <div class="flex items-center gap-4">
                                                <div class="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                                                        <svg class="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                                        </svg>
                                                </div>
                                                <div>
                                                        <p class="text-sm font-medium text-gray-500 m-0">PDPs</p>
                                                        <p class="text-2xl font-semibold text-gray-900 mt-0.5 m-0">{{ stats.themes }}</p>
                                                </div>
                                        </div>
                                </div>

                                <!-- Badges Card -->
                                <div 
                                        :class="[
                                                'bg-white rounded-xl shadow-sm p-5 transition-all duration-150',
                                                canAccessModule(moduleNames.badges) ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : 'opacity-60'
                                        ]"
                                        @click="navigateTo('/badges', moduleNames.badges)"
                                >
                                        <div class="flex items-center gap-4">
                                                <div class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                                                        <svg class="w-6 h-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                                        </svg>
                                                </div>
                                                <div>
                                                        <p class="text-sm font-medium text-gray-500 m-0">Badges</p>
                                                        <p class="text-2xl font-semibold text-gray-900 mt-0.5 m-0">{{ stats.badges }}</p>
                                                </div>
                                        </div>
                                </div>

                                <!-- Stickers Card -->
                                <div 
                                        :class="[
                                                'bg-white rounded-xl shadow-sm p-5 transition-all duration-150',
                                                canAccessModule(moduleNames.stickers) ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : 'opacity-60'
                                        ]"
                                        @click="navigateTo('/sticker', moduleNames.stickers)"
                                >
                                        <div class="flex items-center gap-4">
                                                <div class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                                        <svg class="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                                        </svg>
                                                </div>
                                                <div>
                                                        <p class="text-sm font-medium text-gray-500 m-0">Stickers</p>
                                                        <p class="text-2xl font-semibold text-gray-900 mt-0.5 m-0">{{ stats.flexGroups }}</p>
                                                </div>
                                        </div>
                                </div>
                        </div>

                        <!-- Quick Actions and Recent Activity -->
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <!-- Quick Actions Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-base font-semibold text-gray-900 m-0">Quick Actions</h2>
                                        </div>
                                        <div class="p-6 flex flex-col gap-3">
                                                <button
                                                        type="button"
                                                        :class="[
                                                                'flex items-center gap-3 w-full py-3 px-4 border border-gray-200 bg-white rounded-lg text-sm font-medium text-gray-700 transition-colors duration-150 text-left',
                                                                canAccessModule(moduleNames.pricing) ? 'cursor-pointer hover:bg-gray-50 hover:border-orange-7' : 'opacity-60 cursor-not-allowed'
                                                        ]"
                                                        @click="navigateTo('/pricing', moduleNames.pricing)"
                                                >
                                                        <svg class="w-5 h-5 text-orange-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        View Pricing
                                                </button>
                                                <button
                                                        type="button"
                                                        :class="[
                                                                'flex items-center gap-3 w-full py-3 px-4 border border-gray-200 bg-white rounded-lg text-sm font-medium text-gray-700 transition-colors duration-150 text-left',
                                                                canAccessModule(moduleNames.shipping) ? 'cursor-pointer hover:bg-gray-50 hover:border-orange-7' : 'opacity-60 cursor-not-allowed'
                                                        ]"
                                                        @click="navigateTo('/shipping', moduleNames.shipping)"
                                                >
                                                        <svg class="w-5 h-5 text-orange-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                                        </svg>
                                                        View Shipping
                                                </button>
                                        </div>
                                </div>

                                <!-- System Information Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-base font-semibold text-gray-900 m-0">System Information</h2>
                                        </div>
                                        <div class="p-6 flex flex-col gap-4">
                                                <div class="flex justify-between items-center pb-3 border-b border-gray-100">
                                                        <span class="text-sm text-gray-500">API Status</span>
                                                        <span class="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                                                <span class="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                                                                Connected
                                                        </span>
                                                </div>
                                                <div class="flex justify-between items-center pb-3 border-b border-gray-100">
                                                        <span class="text-sm text-gray-500">Label</span>
                                                        <span class="text-sm font-medium text-gray-900">{{ labelName }}</span>
                                                </div>
                                                <div class="flex justify-between items-center pb-3 border-b border-gray-100">
                                                        <span class="text-sm text-gray-500">Environment</span>
                                                        <span class="text-sm font-medium text-gray-900">{{ stackEnvironment }}</span>
                                                </div>
                                                <div class="flex justify-between items-center">
                                                        <span class="text-sm text-gray-500">Build Number</span>
                                                        <span class="text-sm font-medium text-gray-900">{{ buildNumber }}</span>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, type Ref, type ComputedRef } from 'vue';
import { useRouter, type Router } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { httpClient } from 'utils/http';
import { useModuleAccess } from 'composables/useModuleAccess';

interface Stats {
        productGroups: string;
        offerings: string;
        flexGroups: string;
        themes: string;
        badges: string;
}

interface ModuleNames {
        listerItems: string;
        offerings: string;
        stickers: string;
        pdps: string;
        badges: string;
        pricing: string;
        shipping: string;
}

interface Auth0User {
        nickname?: string;
        name?: string;
}

export default defineComponent({
        name: 'DashboardPage',
        setup() {
                const router: Router = useRouter();
                const auth0 = useAuth0();
                const { canAccessModule } = useModuleAccess();

                const moduleNames: ModuleNames = {
                        listerItems: 'Lister Items',
                        offerings: 'Offering',
                        stickers: 'Stickers',
                        pdps: 'PDPs',
                        badges: 'Badges',
                        pricing: 'Pricing',
                        shipping: 'Shipping',
                };

                const stats: Ref<Stats> = ref({
                        productGroups: '-',
                        offerings: '-',
                        flexGroups: '-',
                        themes: '-',
                        badges: '-',
                });

                const buildNumber: string = (import.meta as unknown as { env: Record<string, string | undefined> }).env.VITE_BUILD_NUMBER || '-';
                const labelName: string = LABEL || 'Unknown';
                const stackEnvironment: string = STACK || 'Unknown';

                const userName: ComputedRef<string> = computed(() => {
                        const user = auth0.user.value as unknown;
                        if (user && typeof user === 'object' && ('nickname' in user || 'name' in user)) {
                                const authUser = user as Auth0User;
                                return authUser.nickname || authUser.name || 'User';
                        }
                        return 'User';
                });

                const navigateTo = (path: string, moduleName: string): void => {
                        if (canAccessModule(moduleName)) {
                                router.push(path);
                        }
                };

                const fetchListerItemsCount = async (): Promise<void> => {
                        try {
                                const response = await httpClient.get('/api/productcategory?limit=1');
                                const totalRecords = response.headers['x-total-records'];
                                if (totalRecords) {
                                        stats.value.productGroups = totalRecords;
                                }
                        } catch (error: unknown) {
                                console.error('Failed to fetch lister items count:', error);
                        }
                };

                const fetchOfferingsCount = async (): Promise<void> => {
                        try {
                                const response = await httpClient.get('/api/offering?limit=1');
                                const totalRecords = response.headers['x-total-records'];
                                if (totalRecords) {
                                        stats.value.offerings = totalRecords;
                                }
                        } catch (error: unknown) {
                                console.error('Failed to fetch offerings count:', error);
                        }
                };

                const fetchStickersCount = async (): Promise<void> => {
                        try {
                                const response = await httpClient.get('/api/sticker?limit=1');
                                const totalRecords = response.headers['x-total-records'];
                                if (totalRecords) {
                                        stats.value.flexGroups = totalRecords;
                                }
                        } catch (error: unknown) {
                                console.error('Failed to fetch stickers count:', error);
                        }
                };

                const fetchPDPsCount = async (): Promise<void> => {
                        try {
                                const response = await httpClient.get('/api/pdp?limit=1');
                                const totalRecords = response.headers['x-total-records'];
                                if (totalRecords) {
                                        stats.value.themes = totalRecords;
                                }
                        } catch (error: unknown) {
                                console.error('Failed to fetch PDPs count:', error);
                        }
                };

                const fetchBadgesCount = async (): Promise<void> => {
                        try {
                                const response = await httpClient.get('/api/badge?limit=1');
                                const totalRecords = response.headers['x-total-records'];
                                if (totalRecords) {
                                        stats.value.badges = totalRecords;
                                }
                        } catch (error: unknown) {
                                console.error('Failed to fetch badges count:', error);
                        }
                };

                onMounted(() => {
                        fetchListerItemsCount();
                        fetchOfferingsCount();
                        fetchStickersCount();
                        fetchPDPsCount();
                        fetchBadgesCount();
                });

                return {
                        stats,
                        userName,
                        buildNumber,
                        labelName,
                        stackEnvironment,
                        navigateTo,
                        canAccessModule,
                        moduleNames,
                };
        },
});
</script>
